import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Spin } from "antd";
import styled from "styled-components";

import { useApi, ApiNames } from "../../hooks/api";
import { Routes } from "../../constant/routes";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import RestoringPatientModal from "../../component/Patient/RestoringPatient/RestoringPatientModal";
import RestoringPatientSkeleton from "../../component/Patient/RestoringPatient/RestoringPatientSkeleton";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
`;

const PatientRedirect = () => {
  const { patientId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [getPatientJourney] = useApi(ApiNames.GetPatientJourney);
  const [restoreArchivedPatient] = useApi(ApiNames.RestoreArchivedPatient);

  const hasDispatched = useRef(false);
  const lastPatientId = useRef(null);
  // In-app opens (archived search row / create-flow "View Patient") carry the archived marker in nav state,
  // so the restore experience can show immediately; a direct URL open has none and must probe first.
  const [isRestoring, setIsRestoring] = useState(location.state?.isArchived === true);
  const [hasError, setHasError] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(false);

  useEffect(() => {
    if (lastPatientId.current !== patientId) {
      hasDispatched.current = false;
      lastPatientId.current = patientId;
    }
    if (!patientId || hasDispatched.current) return;
    hasDispatched.current = true;

    let cancelled = false;
    let redirectTimer;

    // No automatic retry: a restore can be deferred because another worker holds the patient lock, which clears
    // on its own, so opening the patient again is the recovery path — hence "try again later" rather than a dead end.
    const fail = () => {
      if (cancelled) return;
      setHasError(true);
      ErrorMessage("Could not retrieve patient details. Please try again later. Redirecting to dashboard.");
      redirectTimer = setTimeout(() => history.replace(Routes.ROOT), 2000);
    };

    const run = async () => {
      const isArchivedFromNavState = location.state?.isArchived === true;

      // A direct URL open asks for the journey first: that response carries the archived flag, so a live
      // patient costs a single call. An in-app open already knows, and goes straight to the restore rather
      // than asking for a journey the patient cannot have yet.
      let result;
      if (!isArchivedFromNavState) {
        result = await getPatientJourney(patientId);
        if (cancelled) return;
        if (!result) return fail();
      }

      if (isArchivedFromNavState || result.isArchived === true) {
        setIsRestoring(true);
        const restored = await restoreArchivedPatient(patientId);
        if (cancelled) return;
        if (!restored) return fail();

        result = await getPatientJourney(patientId);
        if (cancelled) return;
      }

      if (!result?.journey?.id) return fail();

      // An archived open can request a specific post-restore destination (e.g. edit); default to the journey.
      const base = Routes.PATIENT_JOURNEY_PATH({ patientId, journeyId: result.journey.id });
      const redirect = location.state?.restoreRedirect;
      history.replace({ pathname: `${base}${redirect?.suffix || ""}`, state: redirect?.state });
    };

    run();

    return () => {
      cancelled = true;
      clearTimeout(redirectTimer);
    };
  }, [patientId]);

  if (hasError) {
    return (
      <Container>
        <div>Error loading patient journey. Redirecting...</div>
      </Container>
    );
  }

  if (isRestoring) {
    return (
      <>
        <RestoringPatientSkeleton />
        <RestoringPatientModal visible={!modalDismissed} onClose={() => setModalDismissed(true)} />
      </>
    );
  }

  return (
    <Container>
      <Spin size="large" />
    </Container>
  );
};

export default PatientRedirect;
